import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTheme } from '@mui/material';
import { VerticalScrollableContainer } from 'common/components/container';
import colorPalette from 'common/theme/colorPalette';

import { CXAvatar } from 'common/components/cx-avatar/cx-avatar';
import { RectangularSkeleton } from 'common/components/loaders';
import {
  Badge,
  Card,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
  Button,
  Box,
} from 'common/components/material';
import { CardContent } from 'common/components/material/Card';
import { DrawerHeader } from 'common/components/material/Drawer';
import { BackButton, CloseButton } from 'common/components/navigation';
import { ExploreTabEnum } from 'common/enum/ExploreTab.enum';
import { useDebounce, useDrawer, useInfiniteQuery, useQuery, useSession } from 'common/hooks';
import { createCursorQueryParams, getNextCursorParam } from 'features/org-root';
import InfiniteScrollContainer from 'features/org-root/components/InfiniteScrollContainer';
import { DrawerIdEnum } from 'features/org-root/enums/drawer-id.enum';
import { useGetProfile } from 'features/profiles';
import { Skill } from 'features/skills';
import _ from 'lodash';
import { ClickAwayListener } from '@mui/material';
import moment from 'moment';
import {
  DrawerTitle,
  getDateLabel,
  matchingEngineApi,
  VoteType,
} from '../matching-engine.utils';
import { SkillFeedbackCard } from './SkillFeedbackCard';
import { useGetWorkItemsGroupedByQuarter } from '../hooks/useGetWorkItemsGroupedByQuarter';
import { useGetWorkItems } from '../hooks/useGetWorkItems';

type SubActivity = {
  name: string;
  contributedDays: number;
  lastContributionDate: string;
};

type BaseWorkItem = {
  name: string;
  id: string;
  subActivities: SubActivity[];
  lastContributionDate: string;
  sponsor: string;
  keyOpportunity?: boolean;
  rag?: string;
  projectLink?: string;
};

const WorkItemContribution: React.FC<{
  workItemsGroup: [quarterLabel: string, workItemList: BaseWorkItem[]];
}> = ({ workItemsGroup }) => {
  const { palette } = useTheme();
  const [quarterLabel, workItemList] = workItemsGroup;

  return (
    <Stack spacing={1.5}>
      <Typography
        fontWeight="600"
        color={palette.Text.Headline}
        fontSize="1rem"
        sx={{
          position: 'sticky',
          zIndex: 100,
          top: 0,
          backgroundColor: palette.Container.Bg,
        }}
      >
        {quarterLabel}
      </Typography>
      <WorkItemCardList workItemList={workItemList} />
    </Stack>
  );
};

export const WorkItemCardList: React.FC<{
  workItemList: BaseWorkItem[];
}> = ({ workItemList }) => {
  return (
    <Stack spacing={1.5}>
      {workItemList.map((workItem) => (
        <WorkItemCard key={workItem.name} workItem={workItem} />
      ))}
    </Stack>
  );
};

const WorkItemCard: React.FC<{ workItem: BaseWorkItem }> = ({ workItem }) => {
  const { palette } = useTheme();
  const { name, subActivities, sponsor } = workItem;

  return (
    <Box
      key={workItem.name}
      sx={{
        padding: '5px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack spacing={1}>
        <Typography variant="body2" fontWeight="600" fontSize="0.875rem">
          {name}
        </Typography>
        <Typography variant="body3" fontWeight="400" fontSize="0.875rem">
          Sponsor:{' '}
          <Box
            component="span"
            fontWeight="500"
            fontSize={'0.875rem'}
            color={palette.Text.SubHeadline}
          >
            {sponsor}
          </Box>
        </Typography>

        <Grid
          container
          justifyContent="space-between"
          sx={{ color: palette.Text.SubHeadline }}
        >
          <Grid item md={7}>
            <Typography variant="body3" fontWeight="400" fontSize="0.75rem">
              {'Activity'}
            </Typography>
          </Grid>
          <Grid item md={3}>
            <Typography variant="body3" fontWeight="400" fontSize="0.75rem">
              {'Effort(d)'}
            </Typography>
          </Grid>
          <Grid item md={2} sx={{ textAlign: 'right' }}>
            <Typography variant="body3" fontWeight="400" fontSize="0.75rem">
              {'Latest'}
            </Typography>
          </Grid>
        </Grid>

        {subActivities.map(({ name, contributedDays, lastContributionDate }) => (
          <Grid key={name} container justifyContent="space-between">
            <Grid item md={7}>
              <Typography variant="body2" fontSize="0.875rem" fontWeight="400">
                {name}
              </Typography>
            </Grid>
            <Grid item md={3}>
              <Typography variant="body2" fontSize="0.875rem" fontWeight="400">
                {Math.round(contributedDays)}
              </Typography>
            </Grid>
            <Grid item md={2} sx={{ textAlign: 'right' }}>
              <Typography variant="body2" fontSize="0.875rem" fontWeight="400">
                {moment(lastContributionDate).format('MMM YYYY')}
              </Typography>
            </Grid>
          </Grid>
        ))}
      </Stack>
    </Box>
  );
};

const SearchSkillSkeletonView: React.FC = () => {
  return (
    <>
      {_.range(5).map((key) => (
        <RectangularSkeleton key={key} height="5rem" />
      ))}
    </>
  );
};

const useGetNLPSkillsResult = (params: { term: string; revieweeId: string }) => {
  const { account: user, hasOrganization } = useSession();

  const { term, revieweeId } = params;

  const getRecords = (params: any) => {
    const queryParams = createCursorQueryParams(params.pageParam, {
      term,
      revieweeId,
    });
    return matchingEngineApi.get('nlp-skill-feedbacks', { params: queryParams });
  };

  const queryKey = ['matching-engine', user?.organizationId, 'nlp-skills', term, revieweeId];

  const queryResponse = useInfiniteQuery(queryKey, getRecords, {
    getNextPageParam: getNextCursorParam,
    entityKey: 'skillFeedbacks',
    enabled: hasOrganization,
  });

  const data = queryResponse.isSuccess ? queryResponse.data.pages : [];
  return {
    ...queryResponse,
    data,
  };
};

const SearchSkillOverlay: React.FC<{ onOverlayToggle: Function; profileId: string }> = ({
  onOverlayToggle,
  profileId,
}) => {
  const { palette } = useTheme();
  const [isFocused, setIsFocused] = useState(false);
  const [term, setTerm] = useState<string>();
  const debouncedTerm = useDebounce(term);

  useEffect(() => {
    onOverlayToggle(isFocused);
  }, [isFocused]);

  useEffect(() => {
    if (elementRef.current) {
      const rect = elementRef.current.getBoundingClientRect();
      setWidth(rect.width);
    }
  }, [debouncedTerm, isFocused]);

  const openOverlay = () => {
    setIsFocused(true);
    onOverlayToggle(true);
  };

  const closeOverlay = () => {
    setIsFocused(false);
    onOverlayToggle(false);
  };

  const handleClickAway = () => {
    closeOverlay();
  };

  const elementRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number | null>(null);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Stack position="relative">
        <TextField
          value={term}
          ref={elementRef}
          sx={{
            zIndex: 4000,
            backgroundColor: palette.Background,
            borderRadius: '8px',
            '& .MuiInputBase-root': {
              backgroundColor: 'inherit',
            },
          }}
          placeholder="Search skills to add..."
          onFocus={openOverlay}
          onChange={(e) => setTerm(e.target.value)}
          InputProps={{
            endAdornment: isFocused ? (
              <CloseButton
                sx={{ mr: 2 }}
                variant="text"
                onClick={() => {
                  setTerm('');
                  closeOverlay();
                }}
              />
            ) : (
              <></>
            ),
          }}
        />
        {isFocused && (
          <Stack
            sx={{
              zIndex: 1000,
              left: -15,
              top: -15,
              height: '60vh',
              position: 'absolute',
              width: (width || 0) + 30,
            }}
          >
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'auto',

                '::-webkit-scrollbar': {
                  width: '0.4rem',
                },

                /* Track */
                '::-webkit-scrollbar-track': {
                  boxShadow: 'unset',
                  borderRadius: '0.4rem',
                },

                /* Handle */
                '::-webkit-scrollbar-thumb': {
                  background: colorPalette.GRAY400,
                  borderRadius: '0.4rem',
                },

                /* Handle on hover */
                '::-webkit-scrollbar-thumb:hover': {
                  background: colorPalette.GRAY500,
                },
              }}
            >
              <CardContent>
                <Stack mt={8} spacing={1}>
                  <InfiniteScrollContainer
                    key={ExploreTabEnum.MATCHING_ENGINE}
                    infiniteApiHook={useGetNLPSkillsResult}
                    apiParams={{ term: debouncedTerm, revieweeId: profileId }}
                    InfiniteListContainer={SearchSkillView}
                    infinityListContainerProps={{ profileId }}
                    LoaderComponent={SearchSkillSkeletonView}
                    showFooter={false}
                  />
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        )}
      </Stack>
    </ClickAwayListener>
  );
};

const SearchSkillView = (data: { data: any[]; profileId: string }) => {
  const { data: skillFeedbacks, profileId } = data;
  const { palette } = useTheme();
  if (!data) return <></>;
  const { matchingSkills, relevantSkills } = skillFeedbacks.reduce(
    (acc, skillFeedback) => {
      if (skillFeedback.reason === 'MATCHING_SKILL') {
        acc.matchingSkills.push(skillFeedback);
      } else {
        acc.relevantSkills.push(skillFeedback);
      }
      return acc;
    },
    { matchingSkills: [], relevantSkills: [] },
  );

  if (!relevantSkills.length && !matchingSkills.length) {
    return <Typography>No Skill Found</Typography>;
  }

  if (!relevantSkills.length) {
    return (
      <>
        {(matchingSkills as FeedbackSkill[]).map(
          ({ skill, voteType, feedbackCount, lastFeedbackDate }) => (
            <SkillFeedbackCard
              key={skill.id}
              revieweeId={profileId}
              skill={skill}
              currentVoteType={voteType}
              currentFeedbackCount={feedbackCount}
              canArchive={false}
              skillFeedbackCaptionNode={
                !!voteType ? (
                  <SkillFeedbackCaption
                    profileId={profileId}
                    voteType={voteType}
                    feedbackDate={lastFeedbackDate}
                  />
                ) : (
                  <></>
                )
              }
            />
          ),
        )}
      </>
    );
  }

  return (
    <Stack spacing={4}>
      <Stack spacing={2}>
        <Typography width="100%" p={1} sx={{ backgroundColor: palette.Background }}>
          Matching Skills
        </Typography>
        {(matchingSkills as FeedbackSkill[]).map(
          ({ skill, voteType, feedbackCount, lastFeedbackDate }) => (
            <SkillFeedbackCard
              revieweeId={profileId}
              key={skill.id}
              skill={skill}
              currentVoteType={voteType}
              currentFeedbackCount={feedbackCount}
              canArchive={false}
              skillFeedbackCaptionNode={
                !!voteType ? (
                  <SkillFeedbackCaption
                    profileId={profileId}
                    voteType={voteType}
                    feedbackDate={lastFeedbackDate}
                  />
                ) : (
                  <></>
                )
              }
            />
          ),
        )}
      </Stack>
      <Stack spacing={2}>
        <Typography width="100%" p={1} sx={{ backgroundColor: palette.Background }}>
          Relevant Skills
        </Typography>
        {(relevantSkills as FeedbackSkill[]).map(
          ({ skill, voteType, feedbackCount, lastFeedbackDate }) => (
            <SkillFeedbackCard
              revieweeId={profileId}
              key={skill.id}
              skill={skill}
              currentVoteType={voteType}
              currentFeedbackCount={feedbackCount}
              canArchive={false}
              skillFeedbackCaptionNode={
                !!voteType ? (
                  <SkillFeedbackCaption
                    profileId={profileId}
                    voteType={voteType}
                    feedbackDate={lastFeedbackDate}
                  />
                ) : (
                  <></>
                )
              }
            />
          ),
        )}
      </Stack>
    </Stack>
  );
};

export const ManageSkillDashboardDrawer: React.FC<{
  profileId: any;
}> = ({ profileId }) => {
  const { closeDrawer } = useDrawer();
  const { palette } = useTheme();

  const { data: profile, isLoading } = useGetProfile({ id: profileId });

  if (isLoading) return <></>;

  const closeCurrentDrawer = () => {
    closeDrawer(DrawerIdEnum.MANAGE_SKILL_DASHBOARD_DRAWER);
  };

  return (
    <>
      <DrawerHeader
        sx={{
          borderBottom: `1px solid ${palette.Divider}`,
          padding: 2,
          justifyContent: 'space-between',
          display: 'flex',
        }}
      >
        <Container
          maxWidth={'xxxl'}
          disableGutters
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Stack flexDirection="row" alignItems="center" pl={2} pt={2}>
            <CXAvatar src={profile?.photo} alt={profile?.fullName} />
            <DrawerTitle ml={2}>{profile.fullName}</DrawerTitle>
          </Stack>
          <CloseButton clickHandler={closeCurrentDrawer}></CloseButton>
        </Container>
      </DrawerHeader>
      <Container
        maxWidth={'xxxl'}
        disableGutters
        sx={{ display: 'flex', justifyContent: 'space-between' }}
      >
        <Stack alignItems="center" pt={2} width="100%">
          <ManageSkillDashboardDrawerContent profileId={profileId} />
        </Stack>
      </Container>
    </>
  );
};

const ArchivedSkillsSection: React.FC<{
  isExpanded: boolean;
  setIsExpanded: Dispatch<SetStateAction<boolean>>;
  archivedSkills: FeedbackSkill[];
  profileId: string;
}> = ({ setIsExpanded, isExpanded, archivedSkills, profileId }) => {
  const { palette } = useTheme();
  if (!archivedSkills.length) return null;

  const handleOnClick = () => {
    if (isExpanded) return;
    setIsExpanded(true);
  };

  return (
    <Card
      sx={{ cursor: isExpanded ? 'cursor' : 'pointer', m: '4px' }}
      onClick={handleOnClick}
    >
      <CardContent>
        <Stack gap={2}>
          <Stack display="flex" flexDirection="row" gap={1} alignItems="center">
            {isExpanded && (
              <BackButton variant="text" clickHandler={() => setIsExpanded(false)} />
            )}
            <Typography
              variant="h4"
              color={palette.Text.Headline}
              fontSize="1rem"
              fontWeight="600"
            >
              Archived Skills
            </Typography>
            {!isExpanded && (
              <Badge sx={{ pl: 1 }} color="error" badgeContent={archivedSkills.length} />
            )}
          </Stack>
          {isExpanded && (
            <SkillFeedbackCardList
              skillCategory={archivedSkills}
              profileId={profileId}
              canArchive={false}
            />
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

type FeedbackSkill = {
  skill: Skill;
  lastFeedbackDate: Date | null;
  voteType: VoteType | null;
  feedbackCount: number;
};

const getCategorizedSkills = (skillsDemonstrated: FeedbackSkill[]) => {
  const requiresAttentionSkills: FeedbackSkill[] = [];
  const confirmedSkills: FeedbackSkill[] = [];
  const archivedSkills: FeedbackSkill[] = [];

  for (const demonstratedSkill of skillsDemonstrated) {
    const { voteType, lastFeedbackDate } = demonstratedSkill;

    if (voteType === null) {
      requiresAttentionSkills.push(demonstratedSkill);
      continue;
    }

    if (voteType === null || moment.utc().diff(moment.utc(lastFeedbackDate), 'years') > 1) {
      requiresAttentionSkills.push(demonstratedSkill);
      continue;
    }

    if (voteType === VoteType.ARCHIVE) {
      archivedSkills.push(demonstratedSkill);
      continue;
    }

    if (voteType === VoteType.UPVOTE) {
      confirmedSkills.push(demonstratedSkill);
    }
  }

  return { requiresAttentionSkills, confirmedSkills, archivedSkills };
};

const SkillFeedbackCaption: React.FC<{
  voteType: VoteType | null;
  feedbackDate: Date | null;
  profileId: string;
  skill?: Skill;
}> = ({ feedbackDate, voteType, profileId, skill }) => {
  const { palette } = useTheme();
  const { openDrawer } = useDrawer();
  const openWorkHistoryDrawer = () => {
    openDrawer(DrawerIdEnum.WORK_HISTORY, {
      profileId,
      skill,
    });
  };

  if (!feedbackDate) {
    return (
      <Button onClick={openWorkHistoryDrawer} size="xsmall">
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="caption" fontStyle={'italic'} fontWeight={600}>
            Suggested based on work history
          </Typography>
          <InfoOutlinedIcon sx={{ mt: '2px', fontSize: '13px' }} />
        </Stack>
      </Button>
    );
  }

  const dateLabel = getDateLabel(feedbackDate);

  const getLabel = () => {
    switch (voteType) {
      case VoteType.UPVOTE:
        return `Endorsed ${dateLabel}`;
      case VoteType.DOWNVOTE:
        return `Downvoted ${dateLabel}`;
      case VoteType.ARCHIVE:
        return `Archived ${dateLabel}`;
    }
  };

  return (
    <Stack direction="row" spacing={0.5}>
      <Typography
        variant="caption"
        color={
          moment.utc().diff(moment.utc(feedbackDate), 'days') > 365
            ? palette.SpecialButtons.Delete
            : 'inherit'
        }
        fontStyle={'italic'}
      >
        {getLabel()}
      </Typography>
    </Stack>
  );
};

export const WorkHistoryDrawer: React.FC<{
  profileId: any;
  skill?: Skill;
}> = ({ profileId, skill }) => {
  const { closeDrawer } = useDrawer();
  const { palette } = useTheme();

  const { data: profile } = useGetProfile({ id: profileId });
  const { data: workItemList, isLoading } = useGetWorkItems({
    profileId,
    skillId: skill?.id,
  });

  if (isLoading) return <></>;

  const closeCurrentDrawer = () => {
    closeDrawer(DrawerIdEnum.WORK_HISTORY);
  };

  return (
    <>
      <DrawerHeader
        sx={{
          borderBottom: `1px solid ${palette.Divider}`,
          padding: 2,
          justifyContent: 'space-between',
          display: 'flex',
        }}
      >
        <Container
          maxWidth={'xxxl'}
          disableGutters
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Stack flexDirection="row" alignItems="center" pl={2} pt={2}>
            <CXAvatar src={profile?.photo} alt={profile?.fullName} />
            <DrawerTitle ml={2}>{profile.fullName}</DrawerTitle>
          </Stack>
          <CloseButton clickHandler={closeCurrentDrawer}></CloseButton>
        </Container>
      </DrawerHeader>

      <Stack spacing={2} m={3}>
        <Typography variant="h4" fontWeight={600}>
          {skill?.name || 'Work History'}
        </Typography>
        <VerticalScrollableContainer disableGutters maxHeight="85vh">
          <WorkItemCardList workItemList={workItemList} />
        </VerticalScrollableContainer>
      </Stack>
    </>
  );
};

const SkillFeedbackCardList: React.FC<{
  skillCategory: FeedbackSkill[];
  profileId: string;
  canArchive?: boolean;
}> = ({ skillCategory, profileId, canArchive }) => {
  const { palette } = useTheme();
  return (
    <Stack pl={1}>
      {skillCategory.map(({ skill, voteType, feedbackCount, lastFeedbackDate }, index) => {
        const isFirst = index === 0;
        const isLast = index === skillCategory.length - 1;
        const borderColor = palette.Buttons.Label.LabelDisabled;
        return (
          <div
            style={{
              margin: 0,
              padding: 0,
              border: `1px solid ${borderColor}`,
              borderTopLeftRadius: isFirst ? 12 : 0,
              borderTopRightRadius: isFirst ? 12 : 0,
              borderBottomLeftRadius: isLast ? 12 : 0,
              borderBottomRightRadius: isLast ? 12 : 0,
            }}
          >
            <SkillFeedbackCard
              revieweeId={profileId}
              key={skill.id}
              skill={skill}
              currentVoteType={voteType}
              currentFeedbackCount={feedbackCount}
              skillFeedbackCaptionNode={
                <SkillFeedbackCaption
                  voteType={voteType}
                  feedbackDate={lastFeedbackDate}
                  profileId={profileId}
                  skill={skill}
                />
              }
              canArchive={canArchive}
            />
          </div>
        );
      })}
    </Stack>
  );
};

const ManageSkillDashboardDrawerContent: React.FC<{ profileId: string }> = ({
  profileId,
}) => {
  const { palette } = useTheme();
  const {
    data: skillsDemonstrated,
    isLoading: isSkillsLoading,
    refetch,
  } = useGetAllFeedbackSkills(profileId);

  const [isOverlayVisible, setIsOverlayVisible] = useState(false);

  const [isArchivedSkillSectionExpanded, setIsArchivedSkillSectionExpanded] = useState(false);

  const { requiresAttentionSkills, confirmedSkills, archivedSkills } = getCategorizedSkills(
    skillsDemonstrated || [],
  );

  const [hasWorkHistory, setHasWorkHistory] = useState(false);
  const [isWorkHistoryLoading, setIsWorkHistoryLoading] = useState(true);

  useEffect(() => {
    if (isOverlayVisible) return;
    refetch();
  }, [isOverlayVisible, refetch]);

  const handleOverlayVisibility = (isOverlayVisible: boolean) => {
    setIsOverlayVisible(isOverlayVisible);
  };

  return (
    <Grid
      sx={{
        visibility: isWorkHistoryLoading && isSkillsLoading ? 'hidden' : 'visible',
        px: 4,
        pt: 2,
      }}
      container
      spacing={2}
    >
      <Grid item md={hasWorkHistory ? 8 : 12}>
        <Stack spacing={3}>
          <Typography
            sx={{ opacity: isOverlayVisible ? 0.2 : 1 }}
            variant="h2"
            fontWeight={600}
            color={palette.Text.Headline}
            fontSize="1.25rem"
          >
            Manage Skills
          </Typography>

          <SearchSkillOverlay
            onOverlayToggle={handleOverlayVisibility}
            profileId={profileId}
          />
          <VerticalScrollableContainer
            disableGutters
            maxHeight="75vh"
            sx={{
              maxWidth: '100% !important',
            }}
          >
            <Stack sx={{ opacity: isOverlayVisible ? 0.2 : 1 }} gap={3}>
              <ArchivedSkillsSection
                setIsExpanded={setIsArchivedSkillSectionExpanded}
                isExpanded={isArchivedSkillSectionExpanded}
                archivedSkills={archivedSkills}
                profileId={profileId}
              />
              {!isArchivedSkillSectionExpanded && (
                <>
                  {!!requiresAttentionSkills.length && (
                    <Stack spacing={2} mr={1}>
                      <Stack
                        flexDirection="row"
                        spacing={1.5}
                        sx={{
                          position: 'sticky',
                          zIndex: 100,
                          top: 0,
                          backgroundColor: palette.Container.Bg,
                          p: 1,
                        }}
                      >
                        <Typography
                          color={palette.Text.Headline}
                          variant="h4"
                          fontSize="1rem"
                          fontWeight="600"
                        >
                          Requires Attention{' '}
                        </Typography>
                        <Badge
                          sx={{ pl: 2 }}
                          color="error"
                          badgeContent={requiresAttentionSkills.length}
                        />
                      </Stack>
                      <SkillFeedbackCardList
                        skillCategory={requiresAttentionSkills}
                        profileId={profileId}
                      />
                    </Stack>
                  )}

                  {!!confirmedSkills.length && (
                    <Stack spacing={2} mr={1}>
                      <Stack
                        flexDirection="row"
                        spacing={1.5}
                        sx={{
                          position: 'sticky',
                          top: 0,
                          zIndex: 100,
                          backgroundColor: palette.Container.Bg,
                          p: 1,
                        }}
                      >
                        <Typography
                          variant="h4"
                          fontWeight="600"
                          fontSize="1rem"
                          color={palette.Text.Headline}
                        >
                          Confirmed Skills{' '}
                        </Typography>
                        <Badge
                          sx={{ pl: 2 }}
                          color="primary"
                          badgeContent={confirmedSkills.length}
                        />
                      </Stack>
                      <SkillFeedbackCardList
                        skillCategory={confirmedSkills}
                        profileId={profileId}
                        canArchive={false}
                      />
                    </Stack>
                  )}
                </>
              )}
            </Stack>
          </VerticalScrollableContainer>
        </Stack>
      </Grid>

      <Grid item md={hasWorkHistory ? 4 : 0} sx={{ opacity: isOverlayVisible ? 0.2 : 1 }}>
        {hasWorkHistory && (
          <Typography
            width="inherit"
            variant="h2"
            fontWeight={600}
            color={palette.Text.Headline}
            fontSize="1.25rem"
            margin="1.3rem"
          >
            Work History
          </Typography>
        )}

        <VerticalScrollableContainer maxHeight="80vh" sx={{ mt: 4 }}>
          <InfiniteScrollContainer
            key={ExploreTabEnum.MATCHING_ENGINE}
            infiniteApiHook={useGetWorkItemsGroupedByQuarter}
            apiParams={{ profileId }}
            InfiniteListContainer={WorkHistorySection}
            infinityListContainerProps={{
              profileId,
              setHasWorkHistory: setHasWorkHistory,
              setIsWorkHistoryLoading: setIsWorkHistoryLoading,
            }}
            LoaderComponent={SearchSkillSkeletonView}
            showFooter={false}
          />
        </VerticalScrollableContainer>
      </Grid>
    </Grid>
  );
};

const WorkHistorySection: React.FC<{
  data: [string, BaseWorkItem[]][];
  setHasWorkHistory: (hasWorkHistory: boolean) => {};
  setIsWorkHistoryLoading: (isLoading: boolean) => {};
}> = ({ data, setHasWorkHistory, setIsWorkHistoryLoading }) => {
  useEffect(() => {
    setIsWorkHistoryLoading(!data?.length);
    setHasWorkHistory(!!data?.length);
  }, [data, setIsWorkHistoryLoading, setHasWorkHistory]);

  if (!data.length) return <></>;

  return (
    <Stack spacing={1.5}>
      {data.map(([quarterLabel, workItemList]) => (
        <WorkItemContribution
          key={quarterLabel}
          workItemsGroup={[quarterLabel, workItemList]}
        />
      ))}
    </Stack>
  );
};

const useGetAllFeedbackSkills = (profileId: string) => {
  const { account: user } = useSession();

  const getAllFeedbackSkillsApi = () => {
    const params = {
      profileId,
    };
    return matchingEngineApi.get(`feedback-skills`, { params });
  };

  const queryKey = ['matching-engine', user?.organizationId, profileId, 'feedback-skills'];

  const queryResponse = useQuery(queryKey, getAllFeedbackSkillsApi, {
    enabled: !!profileId,
  });

  if (queryResponse.isSuccess) {
    return {
      ...queryResponse,
      data: queryResponse.data?.data || [],
    };
  }

  return {
    ...queryResponse,
    data: queryResponse.data?.data,
  };
};
